import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Select from 'react-select';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import ModalSection from '../ModalSection';
import CardButton from '../CardButton';
import QuillComponent from '../QuillComponent';
import PaginationMC from '../../../../components/PaginationMC';
import IconMC from '../../../../components/IconMC';
import { CRM_ROUTES } from '../../../../../constants/RoutesConstants';
import CustomFieldModel from '../../../../models/CustomFieldModel';
import CustomTagModel from '../../../../models/CustomTagModel';
import TagModel from '../../../../../components/Tags/TagModel';
import ChatbotOptionModel from '../../../../../components/chatbots/ChatbotOptionModel';

const CRM_CUSTOMERS_PATH = `/retailers/${ENV.SLUG}/customers`;
const linkClassName = 'tw-text-blue-600 hover:tw-text-blue-700';

const SAVED_ITEMS_PAGE_SIZE = 5;

const ClientDataModal = () => {
  const { t } = useTranslation('ChatbotIA');
  const { t: tGeneral } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [cards, setCards] = useState([]);
  const [customFieldOptions, setCustomFieldOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [savedCustomFields, setSavedCustomFields] = useState([]);
  const [savedTags, setSavedTags] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showActionSelector, setShowActionSelector] = useState(false);
  const [customFieldsPage, setCustomFieldsPage] = useState(1);
  const [tagsPage, setTagsPage] = useState(1);
  const [customFieldsTotal, setCustomFieldsTotal] = useState(0);
  const [tagsTotal, setTagsTotal] = useState(0);
  const [loadingSavedCustomFields, setLoadingSavedCustomFields] = useState(false);
  const [loadingSavedTags, setLoadingSavedTags] = useState(false);

  const customSelectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      backgroundColor: '#F7F8FD',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      minHeight: '48px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#E5E7EB'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#6B7280',
      fontSize: '14px',
      fontWeight: '400'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6B7280',
      '&:hover': {
        color: '#6B7280'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  }), []);

  const handleSelectTag = useCallback((option, cardId) => {
    if (option) {
      setCards((prevCards) =>
        prevCards.map((c) =>
          c.id === cardId ? { ...c, selectedTags: [option] } : c
        )
      );
    }
  }, []);

  const removeTag = useCallback((tagToRemove, cardId) => {
    setCards((prevCards) =>
      prevCards.map((c) =>
        c.id === cardId ? {
          ...c,
          selectedTags: (c.selectedTags || []).filter(tag => tag.value !== tagToRemove.value)
        } : c
      )
    );
  }, []);

  useEffect(() => {
    loadClientDataContent();
    loadSavedCustomFields(1);
    loadSavedTags(1);
  }, []);

  const getFieldLabel = useCallback((fieldIdentifier) => {
    const field = customFieldOptions.find(opt => opt.value === fieldIdentifier);
    return field ? field.label : fieldIdentifier;
  }, [customFieldOptions]);

  const loadClientDataContent = () => {
    ChatbotOptionModel.getCustomerColumns()
      .then((response) => {
        const fields = response.fields || [];
        const fieldOptions = fields.map(field => ({
          value: field[1],
          label: field[0]
        }));
        setCustomFieldOptions(fieldOptions);
      })
      .catch((err) => {
        console.error('Error loading customer columns:', err);
      });

    TagModel.get()
      .then((response) => {
        const tags = response.tags || [];
        const tagOpts = tags.map(tag => ({
          value: tag.label || tag.tag,
          label: tag.label || tag.tag
        }));
        setTagOptions(tagOpts);
      })
      .catch((err) => {
        console.error('Error loading tags:', err);
      });

  };

  const getTotalFromResponse = useCallback((response) => {
    return (
      response?.total ??
      response?.meta?.total ??
      response?.pagination?.total ??
      response?.pagination?.total_count ??
      response?.total_count ??
      null
    );
  }, []);

  const loadSavedCustomFields = useCallback(async (page = 1) => {
    setLoadingSavedCustomFields(true);
    try {
      const response = await CustomFieldModel.get({
        limit: SAVED_ITEMS_PAGE_SIZE,
        skip: (page - 1) * SAVED_ITEMS_PAGE_SIZE,
        sort: '-created_at'
      });
      const rawItems = (
        (Array.isArray(response?.data) && response.data) ||
        response?.data?.data ||
        response?.custom_fields ||
        response?.customFields ||
        []
      );
      const items = Array.isArray(rawItems) ? rawItems : [];
      const total = getTotalFromResponse(response) ?? items.length;

      setSavedCustomFields(items);
      setCustomFieldsPage(page);
      setCustomFieldsTotal(total);
    } catch (err) {
      console.error('Error loading saved custom fields:', err);
      showtoast(err?.error || t('config.error_loading'));
      setSavedCustomFields([]);
      setCustomFieldsTotal(0);
    } finally {
      setLoadingSavedCustomFields(false);
    }
  }, [t, getTotalFromResponse]);

  const loadSavedTags = useCallback(async (page = 1) => {
    setLoadingSavedTags(true);
    try {
      const response = await CustomTagModel.get({
        limit: SAVED_ITEMS_PAGE_SIZE,
        skip: (page - 1) * SAVED_ITEMS_PAGE_SIZE,
        sort: '-created_at'
      });
      const rawItems = (
        (Array.isArray(response?.data) && response.data) ||
        response?.data?.data ||
        response?.custom_tags ||
        response?.customTags ||
        []
      );
      const items = Array.isArray(rawItems) ? rawItems : [];
      const total = getTotalFromResponse(response) ?? items.length;

      setSavedTags(items);
      setTagsPage(page);
      setTagsTotal(total);
    } catch (err) {
      console.error('Error loading saved tags:', err);
      showtoast(err?.error || t('config.error_loading'));
      setSavedTags([]);
      setTagsTotal(0);
    } finally {
      setLoadingSavedTags(false);
    }
  }, [t, getTotalFromResponse]);

  const handleDeleteCustomField = useCallback((field) => {
    if (!field.id) {
      console.error('Field ID is missing:', field);
      showtoast(t('config.client_data.delete_error'));
      return;
    }

    CustomFieldModel.delete(field.id)
      .then(() => {
        showtoast(t('config.client_data.delete_success'));
        loadSavedCustomFields(1);
      })
      .catch((err) => {
        console.error('Error deleting custom field:', err);
        showtoast(err.message || t('config.client_data.delete_error'));
      });
  }, [t, loadSavedCustomFields]);

  const handleDeleteTag = useCallback((tag) => {
    CustomTagModel.delete(tag.id)
      .then(() => {
        showtoast(t('config.client_data.delete_success'));
        loadSavedTags(1);
      })
      .catch((err) => {
        console.error('Error deleting tag:', err);
        showtoast(err.message || t('config.client_data.delete_error'));
      });
  }, [t, loadSavedTags]);

  const handleEditCustomField = useCallback((field) => {
    const newCard = {
      id: Date.now(),
      action: 'save_custom_field',
      customField: field.name,
      customFieldInstructions: field.description || '',
      actionPreselected: true,
      isEditing: true,
      editingId: field.id,
      editingName: field.name
    };
    setCards([newCard]);
    setEditingItem({ type: 'custom_field', id: field.id });
  }, []);

  const handleEditTag = useCallback((tag) => {
    const newCard = {
      id: Date.now(),
      action: 'assign_tags',
      selectedTags: [{ value: tag.name, label: tag.name }],
      tagInstructions: tag.description || '',
      actionPreselected: true,
      isEditing: true,
      editingId: tag.id || tag.name,
      editingName: tag.name
    };
    setCards([newCard]);
    setEditingItem({ type: 'custom_tag', id: tag.id || tag.name });
  }, []);

  const handleSave = useCallback(() => {
    const hasValidData = cards.some(card =>
      (card.action === 'save_custom_field' && card.customField) ||
      (card.action === 'assign_tags' && card.selectedTags && card.selectedTags.length > 0)
    );

    if (!hasValidData) {
      showtoast(t('config.client_data.validation_error'));
      return;
    }

    const validationCustomFieldCards = cards.filter(card => card.action === 'save_custom_field' && card.customField);
    for (const card of validationCustomFieldCards) {
      if (card.isEditing && card.editingId) {
        continue;
      }

      const existingField = savedCustomFields.find(field =>
        field.name.toLowerCase() === card.customField.toLowerCase()
      );

      if (existingField) {
        showtoast(t('config.client_data.duplicate_field_error') || `El campo "${card.customField}" ya existe`);
        return;
      }
    }

    const validationTagCards = cards.filter(card => card.action === 'assign_tags' && card.selectedTags && card.selectedTags.length > 0);
    for (const card of validationTagCards) {
      for (const tag of card.selectedTags) {
        if (card.isEditing && card.editingId) {
          continue;
        }

        const existingTag = savedTags.find(savedTag =>
          savedTag.name.toLowerCase() === (tag.value || tag.label).toLowerCase()
        );

        if (existingTag) {
          showtoast(t('config.client_data.duplicate_tag_error') || `La etiqueta "${tag.value || tag.label}" ya existe`);
          return;
        }
      }
    }

    setSaving(true);

    const promises = [];

    // Handle custom fields
    const customFieldCards = cards.filter(card => card.action === 'save_custom_field' && card.customField);
    customFieldCards.forEach(card => {
      const fieldData = {
        name: card.customField,
        description: card.customFieldInstructions || '',
        type: "text"
      };

      if (card.isEditing && card.editingId) {
        promises.push(CustomFieldModel.update(card.editingId, fieldData));
      } else {
        promises.push(CustomFieldModel.save(fieldData));
      }
    });

    // Handle tags
    const tagCards = cards.filter(card => card.action === 'assign_tags' && card.selectedTags && card.selectedTags.length > 0);
    tagCards.forEach(card => {
      card.selectedTags.forEach(tag => {
        const tagData = {
          name: tag.value || tag.label,
          description: card.tagInstructions || ''
        };

        if (card.isEditing && card.editingId) {
          promises.push(CustomTagModel.update(card.editingId, tagData));
        } else {
          promises.push(CustomTagModel.save(tagData));
        }
      });
    });

    Promise.all(promises)
      .then(() => {
        showtoast(editingItem ? t('config.client_data.edit_success') : t('config.save_success'));
        setSaving(false);
        setEditingItem(null);
        setCards([]);
        setShowActionSelector(false);
        loadSavedCustomFields(1);
        loadSavedTags(1);
      })
      .catch((error) => {
        setSaving(false);
        showtoast(error.message || (editingItem ? t('config.client_data.edit_error') : t('config.save_error')));
      });
  }, [cards, editingItem, t, loadSavedCustomFields, loadSavedTags]);

  const addCard = useCallback(() => {
    setShowActionSelector(true);
  }, []);

  const handleActionSelect = useCallback((actionType) => {
    setCards([{
      id: Date.now(),
      action: actionType,
      actionPreselected: true
    }]);
    setShowActionSelector(false);
  }, []);

  const getModalTitle = useMemo(() => {
    if (showActionSelector) {
      return t('config.client_data.choose_action_title');
    }
    const preselectedCard = cards.find(card => card.actionPreselected);
    if (preselectedCard) {
      if (preselectedCard.isEditing) {
        return preselectedCard.action === 'save_custom_field'
          ? t('config.client_data.edit_custom_field_title')
          : t('config.client_data.edit_assign_tag_title');
      } else {
        return preselectedCard.action === 'save_custom_field'
          ? t('config.client_data.save_custom_field_title')
          : t('config.client_data.save_assign_tag_title');
      }
    }
    return t('config.client_data.modal_title');
  }, [showActionSelector, cards, t]);

  const totalCustomFieldsPages = Math.ceil((customFieldsTotal || 0) / SAVED_ITEMS_PAGE_SIZE);
  const totalTagsPages = Math.ceil((tagsTotal || 0) / SAVED_ITEMS_PAGE_SIZE);

  const handleCustomFieldsPageChange = ({ selected }) => {
    loadSavedCustomFields(selected + 1);
  };

  const handleTagsPageChange = ({ selected }) => {
    loadSavedTags(selected + 1);
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={getModalTitle}
        submitAction={cards.some(card => card.actionPreselected) ? handleSave : null}
        submitLabel={cards.some(card => card.actionPreselected) ?
          (cards.some(card => card.isEditing) ? t('config.client_data.update') : t('config.client_data.create'))
          : null}
        submitDisabled={saving}
        showBackButton={showActionSelector || cards.some(card => card.actionPreselected)}
        closeAction={showActionSelector ? () => setShowActionSelector(false) : cards.some(card => card.actionPreselected) ? () => {
          setCards([]);
          setEditingItem(null);
          setShowActionSelector(true);
        } : null}
        cancelAction={showActionSelector ? () => setShowActionSelector(false) : cards.some(card => card.actionPreselected) ? () => {
          setCards([]);
          setEditingItem(null);
          setShowActionSelector(false);
        } : null}
        cancelLabel={showActionSelector ? t('config.client_data.back_button') : cards.some(card => card.actionPreselected) ? t('config.client_data.cancel_button') : null}
        footerVariant={cards.some(card => card.actionPreselected) ? 'VERTICAL' : 'HORIZONTAL'}
        footerClassName={cards.some(card => card.actionPreselected) ? 'custom-vertical-footer' : ''}
      >
        {cards.some(card => card.actionPreselected) ? (
          <>
            {cards.filter(card => card.actionPreselected).map((card) => (
              <div key={card.id}>
                {card.action === 'save_custom_field' && (
                  <div>
                    <div className="figma-field">
                      <label htmlFor={`custom-field-${card.id}`} className="fake-label">
                        {t('config.client_data.custom_field_label')}
                      </label>
                      <Select
                        options={customFieldOptions}
                        value={customFieldOptions.find(option => option.value === card.customField) || null}
                        id={`custom-field-${card.id}`}
                        className="select-tag"
                        classNamePrefix="select-tag"
                        styles={customSelectStyles}
                        placeholder={t('config.client_data.custom_field_placeholder')}
                        noOptionsMessage={() => tGeneral('dropdown_mc.no_options')}
                        onChange={(selectedOption) => {
                          const newCustomField = selectedOption ? selectedOption.value : '';
                          setCards((prevCards) =>
                            prevCards.map((c) =>
                              c.id === card.id ? { ...c, customField: newCustomField } : c
                            )
                          );
                        }}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />
                    </div>
                    <div className="mb-0 mt-5">
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                        {t('config.client_data.instructions_label')}
                      </label>
                      <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
                        {t('config.client_data.instructions_1_description')}
                      </p>
                      <QuillComponent
                        name={`custom_field_instructions_${card.id}`}
                        value={card.customFieldInstructions || ''}
                        onChange={(value) => {
                          setCards((prevCards) =>
                            prevCards.map((c) =>
                              c.id === card.id ? { ...c, customFieldInstructions: value } : c
                            )
                          );
                        }}
                        placeholder={t('config.client_data.custom_field_instructions_placeholder')}
                        overflowHidden={false}
                        style={{ height: 'calc(100vh - 450px)' }}
                      />
                    </div>
                  </div>
                )}

                {card.action === 'assign_tags' && (
                  <div>
                    <div className="figma-field">
                      <label htmlFor={`tags-${card.id}`} className="fake-label">
                        {t('config.client_data.tag_label')}
                      </label>
                      <Select
                        options={tagOptions}
                        value={null}
                        onChange={(option) => handleSelectTag(option, card.id)}
                        placeholder={t('config.client_data.tag_placeholder')}
                        noOptionsMessage={() => tGeneral('dropdown_mc.no_options')}
                        className="select-tag"
                        classNamePrefix="select-tag"
                        styles={customSelectStyles}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />

                      {card.selectedTags && card.selectedTags.length > 0 && (
                        <div className="d-flex flex-wrap mt-10">
                          {card.selectedTags.map((tag, index) => (
                            <div key={`tag-${card.id}-${index}`} className="mr-4 py-5">
                              <div className="bg-light-cian px-10 py-5 border-8 d-flex flex-row">
                                <span className="fz-12 text-blue mr-6 mt-3">{tag.label}</span>
                                <span className="text-blue cursor-pointer mt-3" onClick={() => removeTag(tag, card.id)}>
                                  <i className="mi-close-outline fw-bold" />
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mb-0 mt-5">
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                        {t('config.client_data.instructions_label')}
                      </label>
                      <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
                        {t('config.client_data.instructions_2_description')}
                      </p>
                      <QuillComponent
                        name={`tag_instructions_${card.id}`}
                        value={card.tagInstructions || ''}
                        onChange={(value) => {
                          setCards((prevCards) =>
                            prevCards.map((c) =>
                              c.id === card.id ? { ...c, tagInstructions: value } : c
                            )
                          );
                        }}
                        placeholder={t('config.client_data.tag_instructions_placeholder')}
                        overflowHidden={false}
                        style={{ height: 'calc(100vh - 450px)' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : showActionSelector ? (
          <div className="tw-space-y-4">
            <div onClick={() => handleActionSelect('save_custom_field')}>
              <CardButton
                icon="text-field-outline"
                isIcon={true}
                title={t('config.client_data.save_custom_field')}
                description={t('config.client_data.save_custom_field_description')}
              />
            </div>

            <div onClick={() => handleActionSelect('assign_tags')}>
              <CardButton
                icon="pricetags-outline"
                isIcon={true}
                title={t('config.client_data.save_assign_tag')}
                description={t('config.client_data.save_assign_tag_description')}
              />
            </div>
          </div>
        ) : (
          <>
            <ModalSection
              title={t('config.client_data.section_1_title')}
              description={t('config.client_data.section_1_description')}
              afterContent={
                <>
                  <button
                    type="button"
                    className="tw-w-full tw-px-4 tw-mt-5 tw-py-2 tw-border tw-border-blue-500 tw-text-blue-500 tw-bg-white tw-rounded-lg tw-font-medium tw-text-sm hover:tw-bg-blue-50 tw-transition-colors"
                    onClick={addCard}
                  >
                    {t('config.client_data.button_1')}
                  </button>
                </>
              }
            />

            <ModalSection
              title={t('config.client_data.saved_fields_section_title')}
              description={t('config.client_data.saved_fields_section_description')}
              className="tw-pt-5"
              afterContent={
                <>
                  <div className="tw-flex tw-gap-2 tw-px-4 tw-py-3 tw-mt-3 tw-rounded-xl tw-border tw-border-yellow-100 tw-bg-yellow-50">
                    <IconMC name="info-outline" size="base" className="tw-text-yellow-600 tw-shrink-0 tw-mt-0.5" />
                    <span className="tw-text-xs tw-text-yellow-700 tw-font-medium">
                      <Trans
                        i18nKey="config.client_data.fields_and_tags_hint"
                        ns="ChatbotIA"
                        components={{
                          bold: <strong className="tw-font-semibold" />,
                          crmLink: (
                            <a
                              href={CRM_CUSTOMERS_PATH}
                              className={linkClassName}
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          ),
                          fieldsLink: (
                            <a
                              href={CRM_ROUTES.CUSTOMER_RELATED_FIELDS.urlLinkPath}
                              className={linkClassName}
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          )
                        }}
                      />
                    </span>
                  </div>
                  <div className="tw-mt-6">
                    <h4 className="tw-font-semibold tw-text-gray-950 tw-text-sm tw-mb-3">
                      {t('config.client_data.saved_custom_fields_title')}
                    </h4>
                    {savedCustomFields.length === 0 ? (
                      <p className="tw-bg-gray-50 center-flex tw-p-5 tw-rounded-lg tw-text-blue-500">
                        {t('config.client_data.no_saved_fields')}
                      </p>
                    ) : (
                      <div className="tw-space-y-2">
                        {savedCustomFields.map((field) => (
                          <div
                            key={field.id}
                            className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-rounded-lg tw-bg-gray-50"
                          >
                            <div className="tw-flex-1">
                              <div className="tw-font-medium tw-text-sm tw-text-gray-900">
                                {getFieldLabel(field.name)}
                              </div>
                            </div>
                            <div className="tw-flex tw-gap-2">
                              <button
                                type="button"
                                className="tw-text-blue-500 hover:tw-text-blue-700 tw-transition-colors"
                                onClick={() => handleEditCustomField(field)}
                                title={t('config.client_data.edit_field')}
                              >
                                <i className="mi-edit-outline fz-16"></i>
                              </button>
                              <button
                                type="button"
                                className="tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                                onClick={() => handleDeleteCustomField(field)}
                                title={t('config.client_data.delete_field')}
                              >
                                <i className="mi-trash-2-outline fz-16"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        {totalCustomFieldsPages > 1 && (
                          <div className="tw-mt-3">
                            <PaginationMC
                              onPageChange={handleCustomFieldsPageChange}
                              totalPages={totalCustomFieldsPages}
                              currentPage={customFieldsPage}
                              variant="minimalJustifyCenter"
                              pageRangeDisplayed={2}
                              marginPagesDisplayed={0}
                              disabled={loadingSavedCustomFields}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="tw-mt-6">
                    <h4 className="tw-font-semibold tw-text-gray-950 tw-text-sm tw-mb-3">
                      {t('config.client_data.saved_tags_title')}
                    </h4>
                    <div className="tw-flex tw-gap-2 tw-px-4 tw-py-3 tw-mb-3 tw-rounded-xl tw-border tw-border-yellow-100 tw-bg-yellow-50">
                      <IconMC name="info-outline" size="base" className="tw-text-yellow-600 tw-shrink-0 tw-mt-0.5" />
                      <span className="tw-text-xs tw-text-yellow-700 tw-font-medium">
                        <Trans
                          i18nKey="config.client_data.tags_hint"
                          ns="ChatbotIA"
                          components={{
                            bold: <strong className="tw-font-semibold" />,
                            tagsLink: (
                              <a
                                href={CRM_ROUTES.TAGS.urlLinkPath}
                                className={linkClassName}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            )
                          }}
                        />
                      </span>
                    </div>
                    {savedTags.length === 0 ? (
                      <p className="tw-bg-gray-50 center-flex tw-p-5 tw-rounded-lg tw-text-blue-500">
                        {t('config.client_data.no_saved_tags')}
                      </p>
                    ) : (
                      <div className="tw-space-y-2">
                        {savedTags.map((tag) => (
                          <div
                            key={tag.id || tag.name}
                            className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-rounded-lg tw-bg-gray-50"
                          >
                            <div className="tw-flex-1">
                              <div className="tw-font-medium tw-text-sm tw-text-gray-900">
                                {tag.name}
                              </div>
                            </div>
                            <div className="tw-flex tw-gap-2">
                              <button
                                type="button"
                                className="tw-text-blue-500 hover:tw-text-blue-700 tw-transition-colors"
                                onClick={() => handleEditTag(tag)}
                                title={t('config.client_data.edit_tag')}
                              >
                                <i className="mi-edit-outline fz-16"></i>
                              </button>
                              <button
                                type="button"
                                className="tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                                onClick={() => handleDeleteTag(tag)}
                                title={t('config.client_data.delete_tag')}
                              >
                                <i className="mi-trash-2-outline fz-16"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        {totalTagsPages > 1 && (
                          <div className="tw-mt-3">
                            <PaginationMC
                              onPageChange={handleTagsPageChange}
                              totalPages={totalTagsPages}
                              currentPage={tagsPage}
                              variant="minimalJustifyCenter"
                              pageRangeDisplayed={2}
                              marginPagesDisplayed={0}
                              disabled={loadingSavedTags}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              }
            />
          </>
        )}
      </RightModalLayout>
    </div>
  );
};

export default ClientDataModal;

const style = document.createElement('style');
style.textContent = `
  .custom-vertical-footer .tw-order-2 {
    background-color: #f9fafb !important;
    color: #4b5563 !important;
    border: 1px solid #e5e7eb !important;
  }
  .custom-vertical-footer .tw-order-2:hover {
    background-color: #d1d5db !important;
    color: #374151 !important;
    border-color: #d1d5db !important;
  }
  .custom-vertical-footer .tw-order-2:active {
    background-color: #9ca3af !important;
    border-color: #9ca3af !important;
  }
`;
if (!document.head.querySelector('style[data-custom-footer]')) {
  style.setAttribute('data-custom-footer', 'true');
  document.head.appendChild(style);
}
